import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useCurrency, useAuth } from '@/App';
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Filter, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const categoryLabels = {
  perfume: 'Perfume',
  cream: 'Crema',
  makeup: 'Maquillaje',
  other: 'Otro'
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [paginatedProducts, setPaginatedProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const { formatPrice, formatPYG } = useCurrency();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'perfume',
    brand: '',
    sku: '',
    barcode: '',
    price_pyg: '',
    min_stock: '5',
    supplier: ''
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    let result = products;

    // 1. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q)
      );
    }

    // 2. Filter by Category
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }

    // 3. Filter by Stock Status
    if (stockFilter !== 'all') {
      if (stockFilter === 'low') {
        result = result.filter(p => p.stock > 0 && p.stock <= p.min_stock);
      } else if (stockFilter === 'out') {
        result = result.filter(p => p.stock === 0);
      } else if (stockFilter === 'available') {
        result = result.filter(p => p.stock > p.min_stock);
      }
    }

    setTotalItems(result.length);

    // 4. Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedProducts(result.slice(startIndex, endIndex));

  }, [products, searchQuery, categoryFilter, stockFilter, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, stockFilter]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/`);
      setProducts(response.data);
      // No need to set filteredProducts here as existing useEffect will handle it
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.preventDefault();
    setFormErrors({}); // Clear previous errors

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success('Producto actualizado exitosamente');
      } else {
        await axios.post(`${API}/products/`, formData);
        toast.success('Producto agregado exitosamente');
      }
      setIsAddDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
      fetchProducts();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al guardar producto';

      // Handle specific duplicate errors
      if (detail.includes('SKU')) {
        setFormErrors(prev => ({ ...prev, sku: detail }));
      } else if (detail.includes('Código de Barras')) {
        setFormErrors(prev => ({ ...prev, barcode: detail }));
      }

      toast.error(detail);
    }
  };

  // const handleEdit = (product) => {
  //   setEditingProduct(product);
  //   setFormData({
  //     name: product.name,
  //     description: product.description || '',
  //     category: product.category,
  //     brand: product.brand || '',
  //     sku: product.sku,
  //     barcode: product.barcode || '',
  //     price_usd: product.price_usd.toString(),
  //     stock: product.stock.toString(),
  //     min_stock: product.min_stock.toString(),
  //     supplier: product.supplier || ''
  //   });
  //   setIsAddDialogOpen(true);
  // };


  const handleEdit = async (product) => {
    setEditingProduct(product);

    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      brand: product.brand || '',
      sku: product.sku,
      barcode: product.barcode || '',
      price_pyg: product.price_pyg.toString(),
      stock: product.stock.toString(),
      min_stock: product.min_stock.toString(),
      supplier: product.supplier || ''
    })

    try {
      const response = await axios.get(`${API}/products/${product.id}`);
      setEditingProduct(response.data);
    } catch (error) {
      toast.error('Error al editar producto');
    }
    setIsAddDialogOpen(true);
  }

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este producto?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Producto eliminado exitosamente');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'perfume',
      brand: '',
      sku: '',
      barcode: '',
      price_usd: '',
      stock: '',
      min_stock: '5',
      supplier: ''
    });
    setFormErrors({});
  };

  const canEdit = user?.role === 'Admin' || user?.role === 'Manager';

  const handleExportExcel = () => {
    // 1. Prepare data for Excel
    const dataToExport = products.map(product => ({
      'SKU': product.sku,
      'Nombre': product.name,
      'Categoría': categoryLabels[product.category] || product.category,
      'Marca': product.brand || '',
      'Precio (GS)': product.price_pyg,
      'Stock Total': product.stock,
      'Stock Mínimo': product.min_stock,
      'Estado': product.stock === 0 ? 'Sin Stock' : product.stock <= product.min_stock ? 'Stock Bajo' : 'En Stock',
      'Proveedor': product.supplier || '',
      'Descripción': product.description || ''
    }));

    // 2. Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Auto-width for columns (simple estimation)
    const columnWidths = [
      { wch: 15 }, // SKU
      { wch: 30 }, // Nombre
      { wch: 15 }, // Categoria
      { wch: 15 }, // Marca
      { wch: 12 }, // Precio
      { wch: 10 }, // Stock
      { wch: 10 }, // Min Stock
      { wch: 12 }, // Estado
      { wch: 20 }, // Proveedor
      { wch: 40 }  // Descripcion
    ];
    worksheet['!cols'] = columnWidths;

    // 3. Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");

    // 4. Generate file name with date
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `Productos_Aurelia_${dateStr}.xlsx`;

    // 5. Download file
    XLSX.writeFile(workbook, fileName);
    toast.success('Lista de productos exportada exitosamente');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold" data-font="display">Productos</h1>
        {canEdit && (
          <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
            setIsAddDialogOpen(open);
            if (!open) {
              setEditingProduct(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleExportExcel}
                  className="gap-2"
                >
                  <Download className="size-4" />
                  Exportar Excel
                </Button>
                <Button className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]" data-testid="add-product-button">
                  <Plus className="mr-2 size-4" />
                  Agregar Producto
                </Button>
              </div>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-font="display">{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                <DialogDescription>Por favor, complete los campos siguientes para agregar o editar un producto.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre del Producto *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                      }}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Categoría *</Label>
                    <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                      <SelectTrigger data-testid="product-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="perfume">Perfume</SelectItem>
                        <SelectItem value="cream">Crema</SelectItem>
                        <SelectItem value="makeup">Maquillaje</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brand">Marca</Label>
                    <Input
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU *</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => {
                        setFormData({ ...formData, sku: e.target.value });
                        if (formErrors.sku) setFormErrors({ ...formErrors, sku: null });
                      }}
                      required
                      data-testid="product-sku-input"
                    />
                    {formErrors.sku && <p className="text-sm text-destructive mt-1 font-medium">{formErrors.sku}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="barcode">Código de Barras</Label>
                    <Input
                      id="barcode"
                      value={formData.barcode}
                      onChange={(e) => {
                        setFormData({ ...formData, barcode: e.target.value });
                        if (formErrors.barcode) setFormErrors({ ...formErrors, barcode: null });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price">Precio (GS) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="1"
                      value={formData.price_pyg}
                      onChange={(e) => setFormData({ ...formData, price_pyg: e.target.value })}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock">Stock Mínimo *</Label>
                    <Input
                      id="min_stock"
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                </div>
                {editingProduct && (
                  <Card className="p-4 border-l-4 border-l-[hsl(var(--primary))]">
                    <CardTitle className="text-lg mb-4 flex justify-between items-center">
                      Detalle de Inventario por Lote
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Stock Total</p>
                        <p className="text-2xl font-bold">{editingProduct.stock}</p>
                      </div>
                    </CardTitle>

                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Lote ID</TableHead>
                            <TableHead>Proveedor</TableHead>
                            <TableHead>O. Compra</TableHead>
                            <TableHead className="text-right">Costo Unitario</TableHead>
                            <TableHead className="text-right">Disponible</TableHead>
                            <TableHead>Fecha de Entrada</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {editingProduct.available_lots?.length > 0 ? (
                            editingProduct.available_lots.map((lot) => (
                              <TableRow key={lot.id}>
                                <TableCell className="font-medium text-xs">{lot.id}</TableCell>
                                <TableCell>{lot.supplier_name || 'N/A'}</TableCell>
                                <TableCell>{lot.purchase_order_id || 'N/A'}</TableCell>
                                <TableCell className="text-right font-medium">{formatPYG(lot.cost_pyg)}</TableCell>
                                <TableCell className="text-right font-bold text-lg">{lot.quantity_available}</TableCell>
                                <TableCell>{new Date(lot.entry_date).toLocaleDateString()}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                                No hay lotes disponibles para este producto.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                )}
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="ghost" onClick={() => setIsAddDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]" data-testid="product-edit-save-button">
                    {editingProduct ? 'Actualizar' : 'Agregar'} Producto
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, SKU o marca..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 w-full"
                data-testid="product-search-input"
              />
            </div>

            {/* Find Filters */}
            <div className="flex gap-2 w-full md:w-auto">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[140px]">
                  <div className="flex items-center gap-2">
                    <Filter className="size-3.5" />
                    <SelectValue placeholder="Categoría" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="perfume">Perfume</SelectItem>
                  <SelectItem value="cream">Crema</SelectItem>
                  <SelectItem value="makeup">Maquillaje</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>

              <Select value={stockFilter} onValueChange={setStockFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estado Stock" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="available">En Stock</SelectItem>
                  <SelectItem value="low">Stock Bajo</SelectItem>
                  <SelectItem value="out">Sin Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Marca</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Estado</TableHead>
                  {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedProducts.map((product) => (
                  <TableRow key={product.id} data-testid="product-row">
                    <TableCell className="font-medium">{product.sku}</TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell className="capitalize">{categoryLabels[product.category] || product.category}</TableCell>
                    <TableCell>{product.brand || '-'}</TableCell>
                    <TableCell>{formatPYG(product.price_pyg)}</TableCell>
                    <TableCell>{product.stock}</TableCell>
                    <TableCell>
                      {product.stock === 0 ? (
                        <Badge variant="destructive">Sin Stock</Badge>
                      ) : product.stock <= product.min_stock ? (
                        <Badge className="bg-[#B26A00] text-white">Stock Bajo</Badge>
                      ) : (
                        <Badge className="bg-[#2E7D32] text-white">En Stock</Badge>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            data-testid="product-edit-button"
                          >
                            <Edit className="size-4" />
                          </Button>
                          {user?.role === 'Admin' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(product.id)}
                              data-testid="product-delete-button"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {paginatedProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-muted-foreground">
                      No se encontraron productos
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        {/* Pagination Controls */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} productos
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <div className="text-sm font-medium">
                Página {currentPage} de {Math.ceil(totalItems / itemsPerPage)}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalItems / itemsPerPage), p + 1))}
                disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
